import numeral from 'numeral'
import { DateTime } from 'luxon'

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $filters: {
      formatDate: (value: DateTime | string, locales?: string | Array<string>) => string
      formatIntlNumber: (value: number | string, locales?: string,
        maximumSignificantDigits?: number) => string
      formatNumber: (value: number | string) => string
      formatPercentage: (value: number | string) => string
      formatValue: (value: number | string, valueType: string) => string | number
      formatCurrency: (value: number | string, currencyCode: string) => string
    }
  }
}
type notation = 'compact' | 'standard' | 'scientific' | 'engineering'
interface currencyFormatOptions {
  notation?: notation
  hideSymbol?: boolean
}
const defaultLocale: string = 'en-US'

function formatCurrency(value: string | number, currency = 'USD', opts: currencyFormatOptions = {}) {
  if (typeof value === 'undefined' || value === null) {
    value = '0.00'
  } else {
    value = '' + value
  }
  if (isNaN(parseFloat(value))) {
    return value
  }
  const formatterOptions = {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    notation: 'standard' as notation,
  } as Intl.NumberFormatOptions
  if (opts.notation) {
    formatterOptions.notation = opts.notation
  }
  const formatter = new Intl.NumberFormat(defaultLocale, formatterOptions)
  const ret = formatter.format(parseFloat(value))
  if (opts.hideSymbol) {
    return ret.replace(/[\D.,]+/, '')
  }
  return ret
}
function currencyUSD(value: number | string): string {
  return numeral(value).format('$0,0.00')
}
function formatNumber(value: number | string): string {
  return numeral(value).format('0,0')
}
function formatDate(value: DateTime | string, locales?: string | Array<string>): string {
  const date = typeof value === 'string'
    ? DateTime.fromFormat(value, 'yyyy-MM-dd HH:mm:ss')
    : value
  return new Intl.DateTimeFormat(locales).format(date.toJSDate())
}
function formatIntlNumber(value: number | string, locales?: string,
  maximumSignificantDigits?: number): string {
  if (!maximumSignificantDigits) {
    maximumSignificantDigits = 3
  }
  const number = typeof value === 'number' ? value : +value
  if (!locales) {
    locales = defaultLocale
  }
  return new Intl.NumberFormat(locales, {
    maximumSignificantDigits,
    notation: 'compact',
  })
    .format(number)
}
function formatPercentage(value: number | string): string {
  return numeral(value).format('0.00%')
}
function formatTime(value: number | string): string {
  return numeral(value).format('00:00:00')
}
function formatValue(value: number | string, valueType: string): string | number {
  switch (valueType) {
    case 'currency':
      return currencyUSD(value)
    case 'number':
      return formatNumber(value)
    case 'percentage':
      return formatPercentage(value)
    case 'time':
    case 'duration':
      return formatTime(value)
    default:
      return value
  }
}
export default defineNuxtPlugin(({ vueApp }) => {
  vueApp.config.globalProperties.$filters = {
    formatDate,
    formatCurrency,
    formatIntlNumber,
    formatNumber,
    formatPercentage,
    formatValue,
  }
})
